import { useEffect, useRef, useState } from "react";

/**
 * Returns a ref + boolean indicating whether the element is visible in the
 * viewport. Used to pause CSS-animated tickers when they scroll out of view —
 * saves CPU and reduces competing motion on long pages.
 *
 * Defaults to `true` (visible) so SSR / pre-observer state doesn't flash a
 * frozen ticker on first paint.
 */
export default function useInView(options = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) return;
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0, rootMargin: "0px", ...options }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [options]);

  return [ref, inView];
}
